import { Tooltip } from '@mui/material';
import * as SubframeCore from '@subframe/core';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Avatar } from 'subframe/components/Avatar';
import { formatLocalDate } from 'utils/helper';
import { formatPhoneNumber } from 'utils/phoneNumber';
import endpoints from '../../../constants/endpoints';
import { getCandidateDetail } from '../../../services/recruit';
import { CandidateData } from '../../../types';
import { CandidateHeaderSkeleton } from './CandidateHeaderSkeleton';

interface CandidateHeaderProps {
  candidateId: number;
}

export const CandidateHeader: React.FC<CandidateHeaderProps> = ({ candidateId }) => {
  const {
    data: profile,
    isLoading: isLoadingCandidate,
    error: candidateError,
    isError: isCandidateError,
  } = useQuery({
    queryKey: [endpoints.candidateDetail, candidateId],
    queryFn: () => getCandidateDetail(candidateId),
    enabled: !!candidateId,
    select: (res: any) => res?.result?.candidate_detail,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  if (isLoadingCandidate) {
    return <CandidateHeaderSkeleton />;
  }

  if (isCandidateError) {
    return <div className="p-4 text-red-500">Error loading candidate info.</div>;
  }

  const getFullLocation = (location?: string, region?: string): string => {
    if (!location && !region) return '';
    if (location && region) return `${location}, ${region}`;
    if (location) return location;
    if (region) return region;
    return '';
  };

  const candidate: CandidateData = {
    name: `${profile.first_name ?? ''} ${profile.last_name ?? ''}`,
    initials: `${profile.first_name?.[0] ?? ''}${profile.last_name?.[0] ?? ''}`,
    appliedJobTitle: profile.title,
    jobLocation: profile.location,
    jobRegion: profile.region,
    fullLocation: getFullLocation(profile.location, profile.region),
    birthDate: profile.date_of_birth,
    phone: profile.phone_number,
    email: profile.email,
    applicationStartDate: profile.date_applied,
    applicationDuration: profile.application_duration,
  };

  const handleEmail = () => {
    if (candidate.email) {
      window.open(`mailto:${candidate.email}`, '_blank');
    }
  };

  const handlePhone = () => {
    if (candidate.phone) {
      window.open(`tel:${candidate.phone}`);
    }
  };

  const handleCopyEmail = async () => {
    if (candidate.email) {
      try {
        await navigator.clipboard.writeText(candidate.email || '');
        alert('Email copied to clipboard!');
      } catch {
        alert('Failed to copy email');
      }
    }
  };

  const handleCopyPhone = async () => {
    if (candidate.phone) {
      try {
        await navigator.clipboard.writeText(candidate.phone);
        alert('Phone number copied to clipboard!');
      } catch {
        alert('Failed to copy phone number');
      }
    }
  };

  return (
    <div className="flex w-full flex-col 2xl:flex-row items-start 2xl:items-end justify-between gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-4 lg:pl-4 lg:pr-6 lg:py-4 shadow-sm flex-wrap overflow-hidden">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
        <Avatar className="h-12 w-12 flex-none" image="">
          {candidate.initials}
        </Avatar>
        <div className="flex flex-col 2xl:flex-row items-start 2xl:items-end gap-4 2xl:gap-6 w-full min-w-0">
          <div className="flex flex-col items-start gap-1">
            <div className="flex flex-row items-center gap-2 sm:gap-4 flex-shrink-0">
              <span className="text-header font-header text-neutral-900 whitespace-nowrap">
                {candidate.name}
              </span>
            </div>
            <div className="flex flex-col items-start">
              {candidate.appliedJobTitle && (
                <div className="flex items-center gap-1">
                  <Tooltip title="Applied Position">
                    <span className="text-body-bold font-body-bold text-neutral-500 sm:truncate">
                      {candidate.appliedJobTitle}
                    </span>
                  </Tooltip>
                </div>
              )}
              <div className="flex items-center gap-1 flex-wrap">
                <div className="flex items-center justify-center gap-1 pb-0.5">
                  <Tooltip title="Job Location">
                    <span className="text-label-bold font-label-bold sm:truncate max-w-[120px] sm:max-w-[150px] md:max-w-[200px]">
                      {candidate.fullLocation}
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-start gap-4 w-full min-w-0 overflow-hidden">
            <div className="flex flex-col items-start gap-1 min-h-[48px] justify-between">
              <div className="flex items-center gap-2 min-h-[20px]">
                <SubframeCore.Icon
                  className="text-body font-body text-neutral-500 flex-shrink-0"
                  name="FeatherCalendar"
                />
                <Tooltip title="Date of Birth">
                  {candidate.birthDate ? (
                    <span className="text-label font-label text-neutral-500 sm:truncate">
                      {formatLocalDate(candidate.birthDate)}
                    </span>
                  ) : (
                    <span className="text-label font-label text-neutral-500 sm:truncate">N/A</span>
                  )}
                </Tooltip>
              </div>
              <div className="flex items-center gap-2 min-h-[20px]">
                <SubframeCore.Icon
                  className="text-body font-body text-neutral-500 flex-shrink-0"
                  name="FeatherClock"
                />
                <Tooltip title="Application Duration">
                  {candidate.applicationDuration ? (
                    <span className="text-label font-label text-neutral-500 sm:truncate">
                      {candidate.applicationDuration}
                    </span>
                  ) : (
                    <span className="text-label font-label text-neutral-500 sm:truncate">N/A</span>
                  )}
                </Tooltip>
              </div>
            </div>
            <div className="flex flex-col items-start gap-1 min-h-[48px] justify-between">
              <div className="flex items-center gap-2 min-h-[20px] flex-wrap">
                <SubframeCore.Icon
                  className="text-body font-body text-neutral-500 flex-shrink-0"
                  name="FeatherPhone"
                />
                {candidate.phone ? (
                  <>
                    <Tooltip title="Call">
                      <span
                        className="text-label font-label text-neutral-500 underline cursor-pointer sm:truncate max-w-[120px] sm:max-w-[150px] md:max-w-[180px] break-words"
                        onClick={handlePhone}
                      >
                        {formatPhoneNumber(candidate.phone) || candidate.phone}
                      </span>
                    </Tooltip>
                    <Tooltip title="Copy phone number">
                      <span className="cursor-pointer flex-shrink-0" onClick={handleCopyPhone}>
                        <SubframeCore.Icon
                          className="text-body font-body text-neutral-500"
                          name="FeatherCopy"
                        />
                      </span>
                    </Tooltip>
                  </>
                ) : (
                  <span className="text-label font-label text-neutral-500 sm:truncate max-w-[120px] sm:max-w-[150px] md:max-w-[180px] break-words">
                    N/A
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 min-h-[20px] flex-wrap">
                <SubframeCore.Icon
                  className="text-body font-body text-neutral-500 flex-shrink-0"
                  name="FeatherMail"
                />
                {candidate.email ? (
                  <>
                    <Tooltip title="Send Email">
                      <span
                        className="text-label font-label text-neutral-500 underline cursor-pointer sm:truncate max-w-[120px] sm:max-w-[150px] md:max-w-[200px] lg:max-w-[250px] break-words"
                        onClick={handleEmail}
                      >
                        {candidate.email}
                      </span>
                    </Tooltip>
                    <Tooltip title="Copy email">
                      <span className="cursor-pointer flex-shrink-0" onClick={handleCopyEmail}>
                        <SubframeCore.Icon
                          className="text-body font-body text-neutral-500"
                          name="FeatherCopy"
                        />
                      </span>
                    </Tooltip>
                  </>
                ) : (
                  <span className="text-label font-label text-neutral-500 sm:truncate max-w-[120px] sm:max-w-[150px] md:max-w-[200px] lg:max-w-[250px]">
                    N/A
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-row items-center gap-2 w-full 2xl:w-auto flex-shrink-0">
        <button
          onClick={handleEmail}
          className="flex items-center gap-2 rounded-md border border-solid border-neutral-border px-2 py-2 w-full 2xl:w-auto justify-center hover:bg-neutral-50 transition-colors flex-shrink-0"
          disabled={!!!candidate.email}
        >
          <SubframeCore.Icon className="text-body font-body text-default-font" name="FeatherMail" />
          <span className="text-body font-body text-default-font">Email</span>
        </button>
      </div>
    </div>
  );
};
